import { useState } from 'react';
import { isActiveOnDate } from '@/lib/rrule';
import type { Special } from '@/api/endpoints/bars';
import { CompactSpecialCard } from './CompactSpecialCard';
import { ButtonGroup, ToggleButton } from '@/components/ui';

interface SpecialsSectionProps {
  specials: Special[];
  selectedDate: string;
}

type TypeFilter = 'all' | 'drink' | 'food';

// Parse price string to number for sorting (e.g., "$5.00" -> 5, "2 for $10" -> 10)
function parsePrice(price: string | null): number {
  if (!price) return Infinity; // nulls go last
  const match = price.match(/[\d.]+/);
  return match ? parseFloat(match[0]) : Infinity;
}

export function SpecialsSection({ specials, selectedDate }: SpecialsSectionProps) {
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  // Filter out happy hour specials, filter by date, filter by type, then sort by price
  const filteredSpecials = specials
    .filter((s) => !s.isHappyHour)
    .filter((s) => isActiveOnDate(s.rrule, selectedDate))
    .filter((s) => {
      if (typeFilter === 'all') return true;
      if (typeFilter === 'drink') return s.type === 'drink';
      if (typeFilter === 'food') return s.type === 'food' || s.type === 'combo';
      return true;
    })
    .sort((a, b) => parsePrice(a.price) - parsePrice(b.price));

  // Check if we have any specials at all (before filtering by type)
  const nonHHSpecials = specials
    .filter((s) => !s.isHappyHour)
    .filter((s) => isActiveOnDate(s.rrule, selectedDate));

  if (nonHHSpecials.length === 0) {
    return null;
  }

  // Check what types we have available
  const hasDrinks = nonHHSpecials.some((s) => s.type === 'drink');
  const hasFood = nonHHSpecials.some((s) => s.type === 'food' || s.type === 'combo');
  const showToggle = hasDrinks && hasFood;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">Specials</h2>

        {showToggle && (
          <ButtonGroup>
            <ToggleButton
              isActive={typeFilter === 'drink'}
              onClick={() => setTypeFilter(typeFilter === 'drink' ? 'all' : 'drink')}
            >
              Drink
            </ToggleButton>
            <ToggleButton
              isActive={typeFilter === 'food'}
              onClick={() => setTypeFilter(typeFilter === 'food' ? 'all' : 'food')}
            >
              Food
            </ToggleButton>
          </ButtonGroup>
        )}
      </div>

      {filteredSpecials.length > 0 ? (
        <div className="border border-[var(--color-border)] rounded-lg p-3">
          {filteredSpecials.map((special) => (
            <CompactSpecialCard key={special.id} special={special} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-[var(--color-muted)]">
          No {typeFilter === 'all' ? '' : typeFilter} specials for this day.
        </p>
      )}
    </div>
  );
}
